import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { CalendarCheck, PaperPlaneTilt } from "@phosphor-icons/react";

export function ReportScheduleDialog({ params, reports }) {
  const [open, setOpen] = useState(false);
  const [s, setS] = useState(null);
  const [emails, setEmails] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    api.get("/report-schedule", { params }).then(({ data }) => { setS(data); setEmails(data.recipients.join(", ")); }).catch((e) => toast.error(formatApiError(e)));
  }, [open, params]);
  const body = () => ({ ...params, enabled: s.enabled, reports: s.reports, recipients: emails.split(/[,;\s]+/).filter(Boolean) });
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.put("/report-schedule", body()); setS(data); toast.success(data.enabled ? "Monthly report email scheduled for the last day of each month" : "Schedule saved (disabled)"); }
    catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  const sendNow = async () => {
    setBusy(true);
    try {
      await api.put("/report-schedule", body());
      const { data } = await api.post("/report-schedule/send-now", null, { params });
      data.failed?.length ? toast.warning(`Sent to ${data.sent.length}, failed ${data.failed.length}: ${data.failed[0].error}`) : toast.success(`Reports emailed to ${data.sent.length} recipient(s)`);
      const { data: fresh } = await api.get("/report-schedule", { params }); setS(fresh);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" className="rounded-sm h-9" data-testid="report-schedule-open"><CalendarCheck size={14} className="mr-1" /> Monthly email</Button></DialogTrigger>
      <DialogContent className="max-w-lg" data-testid="report-schedule-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Month-end report email</DialogTitle></DialogHeader>
        {!s ? <div className="text-sm text-zinc-500">Loading…</div> : (
          <div className="space-y-4">
            <div className="text-xs text-zinc-500">On the last day of every month (7 pm IST) the selected reports for that month are emailed as CSV attachments from your company mailbox.</div>
            <div className="flex items-center justify-between"><Label className="text-xs">Enabled</Label><Switch checked={s.enabled} onCheckedChange={(v) => setS({ ...s, enabled: v })} data-testid="report-schedule-enabled" /></div>
            <div><Label className="text-xs">Recipients (comma separated)</Label><Input value={emails} onChange={(e) => setEmails(e.target.value)} placeholder="hr@company.com, finance@company.com" className="rounded-sm mt-1" data-testid="report-schedule-recipients" /></div>
            <div>
              <Label className="text-xs">Reports to include</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 mt-1">
                {reports.map((r) => (
                  <label key={r.key} className="flex items-center gap-2 text-xs py-0.5"><input type="checkbox" checked={s.reports.includes(r.key)} onChange={(e) => setS({ ...s, reports: e.target.checked ? [...s.reports, r.key] : s.reports.filter((k) => k !== r.key) })} data-testid={`report-schedule-${r.key}`} /> {r.label}</label>
                ))}
              </div>
            </div>
            {s.last_result && <div className="text-[11px] text-zinc-500 border border-zinc-200 bg-zinc-50 rounded-sm p-2" data-testid="report-schedule-last">Last sent {new Date(s.last_sent_at).toLocaleString()} · {s.last_result.month} · {s.last_result.sent?.length || 0} delivered, {s.last_result.failed?.length || 0} failed{s.last_result.attachments === false ? " · CSVs omitted (company mailbox not configured)" : ""}</div>}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={sendNow} disabled={busy || !s} className="rounded-sm" data-testid="report-schedule-send-now"><PaperPlaneTilt size={14} className="mr-1" /> Send this month now</Button>
          <Button onClick={save} disabled={busy || !s} className="btn-zenith rounded-sm" data-testid="report-schedule-save">{busy ? "Saving…" : "Save schedule"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
